import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { useState, useEffect, useRef } from 'react'
import { supabase } from '../../supabaseClient'
import {
  LayoutDashboard,
  FolderKanban,
  MessageSquare,
  HardDrive,
  Film,
  FileText,
  Settings,
  LogOut,
} from 'lucide-react'
import OnboardingTour from '../Onboarding/OnboardingTour'
import NotificheBell from '../Layout/NotificheBell'

const navItems = [
  { to: '/client', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/client/progetti', label: 'Progetti', icon: FolderKanban },
  { to: '/client/revisioni', label: 'Revisioni', icon: Film },
  { to: '/client/preventivi', label: 'Preventivi', icon: FileText },
  { to: '/client/files', label: 'File', icon: HardDrive },
  { to: '/client/chat', label: 'Chat', icon: MessageSquare, badge: true },
]

const ClientLayout = () => {
  const { profile, signOut, getDisplayName } = useAuth()
  const navigate = useNavigate()
  const [unreadCount, setUnreadCount] = useState(0)
  const channelRef = useRef(null)
  const myId = profile?.id

  const displayName = getDisplayName()
  const avatarLetter = displayName?.[0]?.toUpperCase() ?? '?'
  const avatarUrl = profile?.avatar_url ? `${profile.avatar_url}?t=${Date.now()}` : null

  const fetchUnread = async () => {
    if (!myId) return
    const { count } = await supabase
      .from('chat_messaggi')
      .select('id', { count: 'exact', head: true })
      .eq('receiver_id', myId)
      .is('letto_at', null)
    setUnreadCount(count ?? 0)
  }

  useEffect(() => {
    if (!myId) return
    fetchUnread()
    if (channelRef.current) supabase.removeChannel(channelRef.current)
    const ch = supabase.channel(`client-unread-${myId}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'chat_messaggi',
        filter: `receiver_id=eq.${myId}`,
      }, () => {
        if (window.location.pathname !== '/client/chat') setUnreadCount(p => p + 1)
      })
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'chat_messaggi',
        filter: `receiver_id=eq.${myId}`,
      }, fetchUnread)
      .subscribe()
    channelRef.current = ch
    return () => { supabase.removeChannel(ch) }
  }, [myId]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex min-h-screen bg-gray-950">
      {/* Sidebar slim */}
      <div className="w-60 h-screen bg-gray-900 border-r border-gray-800 flex flex-col fixed left-0 top-0">

        {/* Logo + badge cliente */}
        <div className="px-5 py-5 border-b border-gray-800">
          <h1 className="text-lg font-bold text-white tracking-tight">Plutonic</h1>
          <span className="inline-block mt-1 text-xs bg-violet-600/20 text-violet-400 border border-violet-600/30 px-2 py-0.5 rounded-full">
            Area cliente
          </span>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map(({ to, label, icon: Icon, badge }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/client'}
              onClick={() => { if (badge) setUnreadCount(0) }}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-violet-600 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                }`
              }
            >
              <div className="relative flex-shrink-0">
                <Icon size={18} />
                {badge && unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-0.5 shadow-md">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </div>
              <span className="flex-1">{label}</span>
              {badge && unreadCount > 0 && (
                <span className="text-[10px] bg-red-500/20 text-red-400 font-semibold px-1.5 py-0.5 rounded-full">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Profilo */}
        <div className="px-3 py-4 border-t border-gray-800">
          <NavLink
            to="/client/impostazioni"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg mb-1 transition-colors ${isActive ? 'bg-gray-800' : 'hover:bg-gray-800'}`
            }
          >
            <div className="w-8 h-8 rounded-full bg-violet-600 flex items-center justify-center text-white text-sm font-bold overflow-hidden flex-shrink-0">
              {avatarUrl
                ? <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" />
                : avatarLetter}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white font-medium truncate">{displayName}</p>
              <p className="text-xs text-gray-500 truncate">{profile?.azienda || 'Cliente'}</p>
            </div>
            <Settings size={14} className="text-gray-500 flex-shrink-0" />
          </NavLink>
          <button
            onClick={() => { signOut(); navigate('/login') }}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-gray-800 transition-colors w-full"
          >
            <LogOut size={18} />
            Esci
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="ml-60 flex-1 flex flex-col min-h-screen">
        {/* Topbar */}
        <div className="flex items-center justify-end px-8 py-3 border-b border-gray-800/50">
          <NotificheBell />
        </div>
        <div className="flex-1 p-8">
          <Outlet />
        </div>
      </div>

      <OnboardingTour />
    </div>
  )
}

export default ClientLayout
